import React from 'react'

import { useResetRoleOverride } from 'hooks/api'
import { getRoleDisplayName } from 'constants/roles'

import { Stack, Typography } from '@mui/material'

import { Modal, Button } from '@repo/ui'

interface ResetRoleModalProps {
  role:
    | { id: string; name: string; builtinRoleId?: string; isOverridden?: boolean; isCustom?: boolean }
    | null
    | undefined
  open: boolean
  onClose: () => void
  onReset?: () => void
}

/**
 * Confirmation modal for reverting a built-in role's company override back to
 * the global default permissions. Deletes the override by its built-in role id.
 */
export const ResetRoleModal = ({ role, open, onClose, onReset }: ResetRoleModalProps) => {
  const resetOverride = useResetRoleOverride()

  const handleReset = async () => {
    if (!role) return
    try {
      await resetOverride.mutateAsync(role.builtinRoleId ?? role.id)
      onClose()
      onReset?.()
    } catch {
      // Error toast is fired by the mutation hook (onError).
    }
  }

  const name = role ? getRoleDisplayName(role as never) : 'this role'

  return (
    <Modal
      title="Reset permissions"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={resetOverride.isPending}>
            Cancel
          </Button>
          <Button onClick={handleReset} disabled={resetOverride.isPending}>
            {resetOverride.isPending ? 'Resetting…' : 'Reset'}
          </Button>
        </>
      }
    >
      <Stack spacing={4} sx={{ lineHeight: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Reset {name} to the built-in default permissions? Any permission changes your company
          made to this role will be discarded.
        </Typography>
      </Stack>
    </Modal>
  )
}
